import { useState } from 'react';
import { OfferRejectionReasonCode, OfferResponse, REJECTION_REASONS } from '../../types/offers';
import {
  combinedRejectReason,
  discountLabel,
  isRejectValid,
  isSuspendable,
  isSuspendValid,
  MIN_REASON_LENGTH,
} from './offerReviewModel';

type Panel = 'none' | 'approve' | 'reject' | 'suspend';

interface DecisionBarProps {
  offer: OfferResponse;
  busy: boolean;
  onApprove: () => void;
  onReject: (reasonCode: OfferRejectionReasonCode, reason: string) => void;
  onSuspend: (reason: string) => void;
}

/**
 * Spec 062 Flow B — Approve (confirm) · Reject (reason picker + text, ≥10 characters together, BR-10)
 * · Suspend (running offers only, reason ≥10 characters).
 */
export default function DecisionBar({ offer, busy, onApprove, onReject, onSuspend }: DecisionBarProps) {
  const [panel, setPanel] = useState<Panel>('none');
  const [reasonCode, setReasonCode] = useState<OfferRejectionReasonCode | ''>('');
  const [rejectText, setRejectText] = useState('');
  const [suspendText, setSuspendText] = useState('');

  const pending = offer.status === 'PENDING_APPROVAL';
  const suspendable = isSuspendable(offer.status) && !offer.revisionOf;
  const rejectValid = isRejectValid(reasonCode, rejectText);
  const rejectLength = combinedRejectReason(reasonCode, rejectText).length;
  const suspendValid = isSuspendValid(suspendText);

  if (!pending && !suspendable) {
    return (
      <div className="admin-card offer-decision" data-testid="offer-decision">
        <p className="admin-text-muted" style={{ margin: 0 }}>No decision is open for this offer.</p>
      </div>
    );
  }

  const close = () => setPanel('none');

  return (
    <div className="admin-card offer-decision" data-testid="offer-decision">
      <div className="offer-decision__buttons">
        {pending && (
          <>
            <button type="button" className="admin-btn admin-btn-success" disabled={busy} onClick={() => setPanel('approve')}>
              Approve
            </button>
            <button type="button" className="admin-btn admin-btn-danger" disabled={busy} onClick={() => setPanel('reject')}>
              Reject
            </button>
          </>
        )}
        {suspendable && (
          <button type="button" className="admin-btn admin-btn-warning" disabled={busy} onClick={() => setPanel('suspend')}>
            Suspend
          </button>
        )}
      </div>

      {panel === 'reject' && (
        <div className="offer-decision__panel" data-testid="reject-panel">
          <label className="admin-form-label" htmlFor="offer-reject-code">Reason</label>
          <select
            id="offer-reject-code"
            className="admin-select"
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value as OfferRejectionReasonCode | '')}
          >
            <option value="">Choose a reason…</option>
            {REJECTION_REASONS.map((r) => (
              <option key={r.code} value={r.code}>{r.label}</option>
            ))}
          </select>
          <label className="admin-form-label" htmlFor="offer-reject-text">Note to the partner</label>
          <textarea
            id="offer-reject-text"
            className="admin-textarea"
            rows={3}
            maxLength={1000}
            placeholder="What should the partner change?"
            value={rejectText}
            onChange={(e) => setRejectText(e.target.value)}
          />
          <div className="offer-decision__hint">
            {rejectValid
              ? 'The partner sees the reason above their offer.'
              : `At least ${MIN_REASON_LENGTH} characters with the reason (${rejectLength}/${MIN_REASON_LENGTH}).`}
          </div>
          <div className="offer-decision__actions">
            <button
              type="button"
              className="admin-btn admin-btn-danger admin-btn-sm"
              disabled={busy || !rejectValid}
              onClick={() => reasonCode && onReject(reasonCode, rejectText.trim())}
            >
              Confirm reject
            </button>
            <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={close} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {panel === 'suspend' && (
        <div className="offer-decision__panel" data-testid="suspend-panel">
          <label className="admin-form-label" htmlFor="offer-suspend-text">Why is this offer suspended?</label>
          <textarea
            id="offer-suspend-text"
            className="admin-textarea"
            rows={3}
            maxLength={1000}
            placeholder="The partner sees this reason."
            value={suspendText}
            onChange={(e) => setSuspendText(e.target.value)}
          />
          <div className="offer-decision__hint">
            {suspendValid
              ? 'The offer leaves the app at once. The partner can edit and resubmit.'
              : `At least ${MIN_REASON_LENGTH} characters (${suspendText.trim().length}/${MIN_REASON_LENGTH}).`}
          </div>
          <div className="offer-decision__actions">
            <button
              type="button"
              className="admin-btn admin-btn-warning admin-btn-sm"
              disabled={busy || !suspendValid}
              onClick={() => onSuspend(suspendText.trim())}
            >
              Confirm suspend
            </button>
            <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={close} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {panel === 'approve' && (
        <div className="admin-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="offer-approve-title">
          <div className="admin-modal">
            <div className="admin-modal-header"><h3 id="offer-approve-title">Approve this offer?</h3></div>
            <div className="admin-modal-body">
              <p style={{ margin: 0 }}>
                <strong>{offer.titleEn}</strong> ({discountLabel(offer)}) goes {offer.revisionOf ? 'onto the running offer' : 'live'} on its start
                date. Customers see it in the app within a minute.
              </p>
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn" onClick={close} disabled={busy}>Cancel</button>
              <button
                type="button"
                className="admin-btn admin-btn-success"
                disabled={busy}
                onClick={() => { close(); onApprove(); }}
              >
                {busy ? 'Approving…' : 'Confirm approve'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
